// S7 — /layouts — manage saved camera grid arrangements.
//
// Cards grid view; the create/edit form opens as an inline panel rather
// than a modal so the user can see existing layouts while building a
// new one (single column on narrow viewports).
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  CheckIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from "lucide-react";

import { api } from "@/api/client";
import { useCameras } from "@/api/cameras";
import {
  GRID_SIZE_OPTIONS,
  type GridSize,
  type LayoutCell,
  type LayoutConfig,
  type LayoutListItem,
  useCreateLayout,
  useDeleteLayout,
  useLayouts,
} from "@/api/layouts";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";


const GRID_CELL_COUNT: Record<GridSize, number> = {
  "1x1": 1,
  "1x2": 2,
  "2x2": 4,
  "2x3": 6,
  "3x3": 9,
  "3x4": 12,
  "4x4": 16,
};

function gridColsClass(size: GridSize): string {
  const cols = parseInt(size.split("x")[1] ?? "2", 10);
  // Tailwind is fine with these literal classnames at build time.
  return (
    {
      1: "grid-cols-1",
      2: "grid-cols-2",
      3: "grid-cols-3",
      4: "grid-cols-4",
    } as Record<number, string>
  )[cols] ?? "grid-cols-2";
}


function GridIconSmall({ size }: { size: GridSize }) {
  const [rows, cols] = size.split("x").map((n) => parseInt(n, 10));
  const cells = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      cells.push(
        <rect
          key={`${r}-${c}`}
          x={1 + c * (14 / cols)}
          y={1 + r * (14 / rows)}
          width={(14 / cols) - 0.5}
          height={(14 / rows) - 0.5}
          fill="currentColor"
          rx="0.5"
        />,
      );
    }
  }
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4">
      {cells}
    </svg>
  );
}


function LayoutCard({
  layout,
  onLoad,
  onSetDefault,
  onDelete,
}: {
  layout: LayoutListItem;
  onLoad: () => void;
  onSetDefault: () => void;
  onDelete: () => void;
}) {
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-sm font-medium">{layout.name}</div>
            <div className="text-xs text-muted-foreground">
              {layout.entity_name ?? "My Layout"}
            </div>
          </div>
          {layout.is_default && (
            <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
              Default
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <GridIconSmall size={layout.grid_size} />
            {layout.grid_size}
          </span>
          <span>{layout.camera_count} cameras</span>
        </div>
        <div className="flex flex-wrap gap-1 pt-1">
          <Button size="sm" variant="outline" onClick={onLoad}>
            Load
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onSetDefault}
            disabled={layout.is_default}
          >
            Set Default
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onDelete}
            className="text-red-600"
          >
            <TrashIcon className="mr-1 h-3.5 w-3.5" /> Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


function CreateLayoutPanel({
  cameras,
  onClose,
  onCreated,
}: {
  cameras: Array<{ id: string; name: string }>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [gridSize, setGridSize] = useState<GridSize>("2x2");
  const [isDefault, setIsDefault] = useState(false);
  const [cellCameraIds, setCellCameraIds] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const create = useCreateLayout();

  const cellCount = GRID_CELL_COUNT[gridSize];

  function handleCellChange(idx: number, cameraId: string) {
    setCellCameraIds((prev) => {
      const next = { ...prev };
      if (cameraId) next[idx] = cameraId;
      else delete next[idx];
      return next;
    });
  }

  async function handleSave() {
    setError(null);
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    const cells: LayoutCell[] = [];
    for (let i = 0; i < cellCount; i++) {
      cells.push({
        position: i,
        camera_id: cellCameraIds[i] ?? null,
        label: "",
      });
    }
    const config: LayoutConfig = { cells };
    try {
      await create.mutateAsync({
        name: name.trim(),
        grid_size: gridSize,
        layout_config: config,
        is_default: isDefault,
      });
      onCreated();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail ?? "Failed to create layout");
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">New layout</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cancel"
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
            placeholder="e.g. Branch HQ — entrance grid"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium">Grid size</label>
          <div className="flex flex-wrap gap-1">
            {GRID_SIZE_OPTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setGridSize(s)}
                className={cn(
                  "inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px]",
                  gridSize === s
                    ? "border-[#ff6d00] bg-[#ff6d00]/10"
                    : "border-border bg-background hover:bg-muted",
                )}
              >
                <GridIconSmall size={s} />
                <span>{s}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium">
            Cameras ({cellCount} slots)
          </label>
          <div className={cn("grid gap-1", gridColsClass(gridSize))}>
            {Array.from({ length: cellCount }).map((_, idx) => (
              <select
                key={idx}
                value={cellCameraIds[idx] ?? ""}
                onChange={(e) => handleCellChange(idx, e.target.value)}
                className="rounded border border-border bg-background px-1.5 py-1 text-[11px]"
              >
                <option value="">Empty</option>
                {cameras.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          Make this my default
        </label>

        {error && (
          <div className="rounded bg-red-50 px-2 py-1 text-xs text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void handleSave()}>
            <CheckIcon className="mr-1 h-3.5 w-3.5" /> Save layout
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


export default function LayoutManager() {
  const { data: layouts, isLoading, isError, refetch } = useLayouts();
  const { data: cameras } = useCameras({ limit: 200 });
  const del = useDeleteLayout();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  const cameraList = useMemo(
    () =>
      (cameras?.items ?? []).map((c) => ({
        id: c.id,
        name: c.name,
      })),
    [cameras],
  );

  // We can't call useUpdateLayout(id) inside .map() (hook count would
  // change between renders). For the single "set default" mutation an
  // imperative PUT + cache invalidation is simpler than a state machine.
  async function handleSetDefault(layout: LayoutListItem) {
    await api.put(`/api/v1/layouts/${layout.id}`, { is_default: true });
    await qc.invalidateQueries({ queryKey: ["layouts"] });
  }

  function handleLoad(layout: LayoutListItem) {
    nav(`/live?layout=${encodeURIComponent(layout.id)}`);
  }

  async function handleDelete(layout: LayoutListItem) {
    if (!window.confirm(`Delete layout "${layout.name}"?`)) return;
    await del.mutateAsync(layout.id);
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Layouts</h1>
          <p className="text-sm text-muted-foreground">
            Saved camera grid arrangements. Load any layout into{" "}
            <Link to="/live" className="text-[#ff6d00] underline">
              Live View
            </Link>
            .
          </p>
        </div>
        <Button onClick={() => setCreating(true)} disabled={creating}>
          <PlusIcon className="mr-1 h-4 w-4" /> New layout
        </Button>
      </div>

      {creating && (
        <CreateLayoutPanel
          cameras={cameraList}
          onClose={() => setCreating(false)}
          onCreated={() => setCreating(false)}
        />
      )}

      {isError ? (
        <p className="text-sm text-destructive">
          Could not load layouts.{" "}
          <button
            type="button"
            onClick={() => refetch()}
            className="underline underline-offset-2"
          >
            Retry
          </button>
        </p>
      ) : isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Loading layouts…
          </CardContent>
        </Card>
      ) : !layouts || layouts.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No saved layouts yet. Click <strong>New layout</strong> to start.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {layouts.map((l) => (
            <LayoutCard
              key={l.id}
              layout={l}
              onLoad={() => handleLoad(l)}
              onSetDefault={() => void handleSetDefault(l)}
              onDelete={() => void handleDelete(l)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
